import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaGithub, 
  FaExternalLinkAlt, 
  FaCode,
  FaRocket,
  FaArrowRight
} from 'react-icons/fa';
import { apiService } from '../../services/api';

const FeaturedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await apiService.getFeaturedProjects();
        setProjects(response.data || []);
      } catch (error) {
        console.error('Error fetching featured projects:', error);
        // Fallback data
        setProjects([
          {
            id: 1,
            title: 'HealthGuardX',
            description: 'Decentralized Healthcare Identity Platform using blockchain and IPFS',
            tech_stack: ['Solidity', 'React', 'TypeScript', 'Solana', 'IPFS'],
            github_url: 'https://github.com/developer13-art/HealthGuard',
            live_url: 'https://healthguard-1-pba4.onrender.com',
            hero_image: '/assets/images/healthguardx.jpg'
          },
          {
            id: 2,
            title: 'FYP Assist Hub',
            description: 'Digital marketplace connecting students with supervisors and developers',
            tech_stack: ['React', 'Node.js', 'PostgreSQL', 'Tailwind CSS'],
            github_url: '#',
            live_url: '#',
            hero_image: '/assets/images/fyp-hub.jpg'
          },
          {
            id: 3,
            title: 'Emergency Alert System',
            description: 'Web platform with embedded hardware for emergency response',
            tech_stack: ['React', 'Laravel', 'MySQL', 'IoT', 'GPS'],
            github_url: '#',
            live_url: '#',
            hero_image: '/assets/images/emergency-system.jpg'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  if (loading) {
    return (
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 animate-pulse">
                <div className="h-48 bg-white/10 rounded-xl mb-4"></div>
                <div className="h-6 bg-white/10 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-white/10 rounded w-full mb-2"></div>
                <div className="h-4 bg-white/10 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            Featured Work
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold">
            <span className="gradient-text">Featured</span> Projects
          </h2>
          <p className="text-gray-400 mt-2 max-w-2xl mx-auto">
            Some of my most impactful projects that showcase my skills and expertise
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-primary-500/10"
            >
              <div className="relative h-48 bg-gradient-to-br from-primary-500/20 to-accent-500/20 overflow-hidden">
                {project.hero_image ? (
                  <img 
                    src={project.hero_image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FaCode className="text-5xl text-white/20" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-dark-950 via-transparent to-transparent" />
              </div>

              <div className="p-6">
                <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-primary-400 transition-colors duration-300">
                  {project.title}
                </h3>
                
                <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                  {project.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tech_stack && project.tech_stack.slice(0, 4).map((tech, i) => (
                    <span 
                      key={i}
                      className="px-2 py-1 text-xs rounded-md bg-white/5 border border-white/10 text-gray-400"
                    >
                      {tech}
                    </span>
                  ))}
                  {project.tech_stack && project.tech_stack.length > 4 && (
                    <span className="px-2 py-1 text-xs rounded-md bg-white/5 border border-white/10 text-gray-400">
                      +{project.tech_stack.length - 4}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex gap-2">
                    {project.github_url && (
                      <a 
                        href={project.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-white"
                      >
                        <FaGithub size={16} />
                      </a>
                    )}
                    {project.live_url && (
                      <a 
                        href={project.live_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-all duration-300 text-gray-400 hover:text-white"
                      >
                        <FaExternalLinkAlt size={16} />
                      </a>
                    )}
                  </div>
                  
                  <Link 
                    to={`/projects/${project.id}`}
                    className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors duration-300"
                  >
                    View Details
                    <FaArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <Link to="/projects">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 border border-primary-500/30 rounded-xl text-primary-400 hover:bg-primary-500/10 transition-all duration-300 flex items-center gap-2 mx-auto"
            >
              View All Projects
              <FaRocket />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedProjects;